<template>
  <vue-tel-input
    ref="telInput"
    v-model="phoneValue"
    :input-options="phoneInputOptions"
    mode="international"
    :auto-format="false"
    default-country="FI"
    :auto-default-country="false"
    @country-changed="onCountryChanged"
  ></vue-tel-input>
</template>

<script>
// Note: vue-tel-input's own flag/country detection only updates once the typed
// number is a complete, strictly-valid number for that country - a manually
// typed number with a foreign prefix (e.g. "+44...") would otherwise keep
// showing the default/previous country's flag until every digit is entered.
// This is patched at the library level (see patches/vue-tel-input+9.8.0.patch)
// to also recognize the calling code as soon as it's identifiable, so the
// flag updates progressively while typing.
import { VueTelInput } from 'vue-tel-input'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

export default {
  name: 'PhoneNumberInput',
  components: { VueTelInput },
  props: {
    modelValue: { type: String, default: '' },
    placeholder: { type: String, default: '' }
  },
  emits: ['update:modelValue'],
  computed: {
    phoneValue: {
      get () {
        return this.modelValue
      },
      set (newValue) {
        this.$emit('update:modelValue', newValue)
      }
    },
    phonePlaceholder () {
      return this.placeholder || this._('Enter phone number')
    },
    // vue-tel-input has no top-level "placeholder" prop - it only reads
    // inputOptions.placeholder (defaulting to the hardcoded, untranslated
    // "Enter a phone number"). Passing our own input-options object
    // replaces the library's defaults entirely, so the other keys below
    // are copied from vue-tel-input's own defaults to avoid regressions.
    phoneInputOptions () {
      return {
        autocomplete: 'on',
        autofocus: false,
        maxlength: 25,
        name: 'telephone',
        placeholder: this.phonePlaceholder,
        readonly: false,
        required: false,
        showDialCode: false,
        tabindex: 0,
        type: 'tel'
      }
    }
  },
  methods: {
    // Workaround for a vue-tel-input bug (still present in the latest 9.8.0):
    // selecting a country from the flag dropdown while the number starts
    // with "+" reformats the number internally but never propagates it
    // through v-model. Recompute and re-emit it ourselves.
    onCountryChanged (country) {
      if (!country || !country.iso2 || !this.phoneValue || this.phoneValue[0] !== '+') {
        return
      }
      const parsed = parsePhoneNumberFromString(this.phoneValue)
      if (!parsed || !parsed.nationalNumber) {
        return
      }
      const reformatted = parsePhoneNumberFromString(parsed.nationalNumber, country.iso2)
      if (reformatted) {
        this.phoneValue = reformatted.formatInternational()
      }
    },
    focus () {
      this.$refs.telInput?.focus()
    },
    blur () {
      this.$refs.telInput?.blur()
    }
  }
}
</script>
